/**
 * The batch run to bring back after a page refresh.
 *
 * Only one batch runs at a time (the app-wide run window owns it), so this is
 * a single saved slot. It holds what the create-202 returned — nothing on the
 * server returns a batch, so this is the only record of what was submitted.
 *
 * It is a hint, never the truth: on load the run history and fresh per-script
 * reads decide whether the batch is still running before anything reopens.
 *
 * Browser storage can be unavailable (private windows, blocked site data), so
 * every access is guarded — losing the slot only means the run window does not
 * come back after a refresh.
 *
 * @module features/executionStudio/utils/batchSession
 */

const KEY = 'cliffi-active-batch';

/** After this the saved batch is ignored and dropped — the run is long over. */
export const BATCH_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

/** Remember the batch that just started. */
export const saveBatchSession = (session) => {
  if (!session?.batchId) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...session, savedAt: Date.now() }));
  } catch {
    // Storage unavailable — the run still works, it just will not survive a refresh
  }
};

/** The saved batch, or null when there is none, it is unreadable, or it has expired. */
export const loadBatchSession = (now = Date.now()) => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.batchId || !Array.isArray(session.members)) {
      localStorage.removeItem(KEY);
      return null;
    }
    if (!session.savedAt || now - session.savedAt > BATCH_SESSION_TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
};

/** Forget the saved batch — it finished, was closed, or was replaced. */
export const clearBatchSession = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to clear
  }
};
